import { useRef, useState } from 'react';
import './PayStubModal.css';
import { useTranslation } from 'react-i18next';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Button, Typography, Row, Col, Card, Space, Avatar, Divider, Descriptions } from 'antd';
import { DownloadOutlined, UserOutlined, BankOutlined, PhoneOutlined, IdcardOutlined, DollarOutlined, ClockCircleOutlined, FileTextOutlined, PlusOutlined, MinusOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const formatNumber = (number, locale) => {
  if (isNaN(number)) {
    return '0.00';
  }
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(number);
};

const formatDate = (dateString, locale) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleString(locale, { month: 'short' });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const PayStubModal = ({ payStubData }) => {
  const { t, i18n } = useTranslation();
  const componentRef = useRef();
  const [isButtonHidden, setIsButtonHidden] = useState(false);
  const [logoError, setLogoError] = useState(false);

  if (!payStubData) {
    return null;
  }

  const formatPayPeriod = (periodString) => {
    // Handle YYYY-MM format
    if (periodString && periodString.match(/^\d{4}-\d{2}$/)) {
      const [year, month] = periodString.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleString(i18n.language, { month: 'long', year: 'numeric' });
    }
    
    // Handle date range format if still exists
    if (periodString && periodString.includes('_')) {
      const [startDateStr, endDateStr] = periodString.split('_');
      const startParts = startDateStr.split('-').map(Number);
      const endParts = endDateStr.split('-').map(Number);
      
      const startDate = new Date(startParts[0], startParts[1] - 1, startParts[2]);
      const endDate = new Date(endParts[0], endParts[1] - 1, endParts[2]);
      
      const startFormatted = startDate.getDate() + ' ' + 
                            startDate.toLocaleString(i18n.language, { month: 'short' });
      
      const endFormatted = endDate.getDate() + ' ' + 
                          endDate.toLocaleString(i18n.language, { month: 'short' });
      
      return `${startFormatted} - ${endFormatted}`;
    }
    
    return periodString;
  };

  const handleDownloadPDF = () => {
    setIsButtonHidden(true);
    const element = componentRef.current;

    html2canvas(element).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      pdf.addImage(imgData, 'PNG', 10, 10, 190, 0);
      pdf.save(`paystub_${payStubData.teacherName}_${payStubData.payPeriod}.pdf`);
      setIsButtonHidden(false);
    });
  };

  return (
    <div className="paystub-content" ref={componentRef}>
      {/* Header Card */}
      <Card 
        className="paystub-header-card" 
        bordered={false}
        style={{ marginBottom: 24 }}
      >
        <Row gutter={[24, 16]} align="middle">
          <Col xs={24} sm={12} md={8} lg={6}>
            {payStubData.companyLogo && !logoError ? (
              <img
                src={payStubData.companyLogo}
                alt="Company Logo"
                className="company-logo"
                style={{ maxWidth: '120px', height: 'auto' }}
                onError={() => setLogoError(true)}
              />
            ) : (
              <Avatar shape="square" size={100} icon={<BankOutlined />} />
            )}
          </Col>
          
          <Col span={10}>
            <div style={{ fontSize: '13px', lineHeight: 1.3 }}>
              <Title level={5} style={{ margin: '0 0 4px 0', fontSize: '14px' }}>
                {payStubData.companyName || 'Company Name'}
              </Title>
              <div>{payStubData.companyAddress || 'Company Address'}</div>
              <div>{payStubData.companyWebsite || 'Company Website'}</div>
              <div>{payStubData.companyTax || 'Company Tax'}</div>
              <div>{payStubData.companyPhone || 'Company Phone'}</div>
            </div>
          </Col>
          
          <Col span={6}>
            <div style={{ textAlign: 'right', fontSize: '11px' }}>
              <div style={{ marginBottom: 4 }}>
                <Text strong style={{ fontSize: '14px' }}>{t('Period')}</Text>
                <br />
                <Text style={{ fontSize: '14px' }}>
                  {formatPayPeriod(payStubData.payPeriod) || 'N/A'}
                </Text>
              </div>
              <div>
                <Text strong style={{ fontSize: '14px' }}>{t('Pay Date')}</Text>
                <br />
                <Text style={{ fontSize: '14px' }}>
                  {payStubData.payDate ? formatDate(payStubData.payDate) : 'N/A'}
                </Text>
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Employee Info */}
      <Card 
        title={<Space><UserOutlined /> {t('Employee Info')}</Space>}
        style={{ marginBottom: 24 }}
        size="small"
      >
        <Row gutter={[16, 8]}>
          <Col xs={24} sm={12}>
            <Space>
              <UserOutlined />
              <Text strong>{t('name')}:</Text>
              <Text>{payStubData.teacherName || 'N/A'}</Text>
            </Space>
          </Col>
          <Col xs={24} sm={12}>
            <Space>
              <IdcardOutlined />
              <Text strong>{t('nationalID')}:</Text>
              <Text>{payStubData.teacherNationalId || 'N/A'}</Text>
            </Space>
          </Col>
          <Col xs={24} sm={12}>
            <Space>
              <BankOutlined />
              <Text strong>{t('address')}:</Text>
              <Text>{payStubData.teacherAddress || 'N/A'}</Text>
            </Space>
          </Col>
          <Col xs={24} sm={12}>
            <Space>
              <PhoneOutlined />
              <Text strong>{t('phone')}:</Text>
              <Text>{payStubData.teacherPhone || 'N/A'}</Text>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Work Summary */}
      <Card 
        title={<Space><ClockCircleOutlined /> {t('Work Summary')}</Space>}
        style={{ marginBottom: 24 }}
        size="small"
      >
        <Row gutter={[24, 16]}>
          <Col xs={12} sm={8}>
            <div style={{ textAlign: 'center' }}>
              <Title level={3} style={{ margin: '0 0 4px 0', color: '#5A9D8A' }}>
                {payStubData.totalHours || 0}
              </Title>
              <Text type="secondary">{t('Total Hours')}</Text>
            </div>
          </Col>
          <Col xs={12} sm={8}>
            <div style={{ textAlign: 'center' }}>
              <Title level={3} style={{ margin: '0 0 4px 0', color: '#5A9D8A' }}>
                {payStubData.totalClasses || 0}
              </Title>
              <Text type="secondary">{t('Total Classes')}</Text>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center' }}>
              <Title level={4} style={{ margin: '0 0 4px 0', color: '#5A9D8A' }}>
                ฿{formatNumber(payStubData.subAmount || 0)}
              </Title>
              <Text type="secondary">{t('Base Amount')}</Text>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Payment Calculation */}
      <Card 
        title={<Space><DollarOutlined /> {t('Payment Calculation')}</Space>}
        size="small"
        style={{ marginBottom: 16 }}
      >
        {/* Base Amount */}
        <Row justify="space-between" style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
          <Col>
            <Text strong>{t('Base Amount')}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {t('Total earnings from classes')}
            </Text>
          </Col>
          <Col>
            <Text style={{ fontSize: '16px' }}>฿{formatNumber(payStubData.subAmount || 0)}</Text>
          </Col>
        </Row>

        {/* Additions */}
        {payStubData.otherAddition > 0 && (
          <Row justify="space-between" style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
            <Col>
              <Space>
                <PlusOutlined style={{ color: '#52c41a' }} />
                <div>
                  <Text strong style={{ color: '#52c41a' }}>{t('Other Addition')}</Text>
                  <br />
                  {payStubData.otherAdditionNote && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {payStubData.otherAdditionNote}
                    </Text>
                  )}
                </div>
              </Space>
            </Col>
            <Col>
              <Text style={{ fontSize: '16px', color: '#52c41a' }}>
                +฿{formatNumber(payStubData.otherAddition)}
              </Text>
            </Col>
          </Row>
        )}

        {/* Gross Total */}
        <Row justify="space-between" style={{ padding: '12px 0', borderBottom: '2px solid #1890ff', backgroundColor: '#f0f8ff' }}>
          <Col>
            <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>{t('Gross Total')}</Text>
          </Col>
          <Col>
            <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
              ฿{formatNumber(payStubData.grossTotal || 0)}
            </Text>
          </Col>
        </Row>

        {/* Deductions */}
        <div style={{ margin: '16px 0' }}>
          <Title level={5} style={{ margin: '0 0 12px 0' }}>
            <MinusOutlined style={{ color: '#ff4d4f' }} /> {t('Deductions')}
          </Title>

          {payStubData.withholdingTaxApplied && payStubData.withholdingTaxAmount > 0 && (
            <Row justify="space-between" style={{ padding: '8px 0' }}>
              <Col>
                <Text>{t('Withholding Tax')} (3%)</Text>
              </Col>
              <Col>
                <Text style={{ color: '#ff4d4f' }}>
                  -฿{formatNumber(payStubData.withholdingTaxAmount)}
                </Text>
              </Col>
            </Row>
          )}

          {payStubData.socialSecurityDeduction > 0 && (
            <Row justify="space-between" style={{ padding: '8px 0' }}>
              <Col>
                <Text>{t('Social Security')}</Text>
              </Col>
              <Col>
                <Text style={{ color: '#ff4d4f' }}>
                  -฿{formatNumber(payStubData.socialSecurityDeduction)}
                </Text>
              </Col>
            </Row>
          )}

          {payStubData.otherDeduction > 0 && (
            <Row justify="space-between" style={{ padding: '8px 0' }}>
              <Col>
                <div>
                  <Text>{t('Other Deduction')}</Text>
                  <br />
                  {payStubData.otherDeductionNote && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {payStubData.otherDeductionNote}
                    </Text>
                  )}
                </div>
              </Col>
              <Col>
                <Text style={{ color: '#ff4d4f' }}>
                  -฿{formatNumber(payStubData.otherDeduction)}
                </Text>
              </Col>
            </Row>
          )}
        </div>

        <Divider style={{ margin: '16px 0' }} />

        {/* Net Total */}
        <div >
            <div style={{ fontSize: '14px', color: '#9ca3af', textTransform: 'uppercase', textAlign: 'center', letterSpacing: '1px', marginBottom: '8px' }}>
              Net Pay
            </div>
            <div style={{ fontSize: '24px', fontWeight: 700, textAlign: 'center' }}>
              ฿{formatNumber(payStubData.netTotal || 0)}
            </div>
          </div>
      </Card>

      {/* Download Button */}
      {!isButtonHidden && (
        <div className="no-print" style={{ textAlign: 'center', marginTop: 24 }}>
          <Button 
            type="primary" 
            icon={<DownloadOutlined />} 
            onClick={handleDownloadPDF}
            size="small"
          >
          </Button>
        </div>
      )}
    </div>
  );
};

export default PayStubModal;